<template>
  <div class="constrain-width || mobile">
    <PageHeader
      :headerArch="pageType === pageTypes.therapistClientPayment && !error"
    />
    <PagePaymentError v-if="error" :errorMessage="error" />
    <PageTherapistClientSuccess
      v-else-if="pageType === pageTypes.paymentSuccess"
      :key="`${pageTypes.paymentSuccess}-${pageId}`"
      :content="pageData"
      :action="() => setCurrentPage(defaultProgression)"
    />
    <PageTherapistClientPaymentDownload
      v-else-if="pageType === pageTypes.paymentDownload"
      :key="`${pageTypes.paymentDownload}-${pageId}`"
      :content="pageData"
    />
    <PageTherapistClientPaymentPage
      v-else-if="pageType === pageTypes.therapistClientPayment"
      :updateFormState="updateFormState"
      :submitFormData="submitStripeProcess"
      :formData="therapistClientForm"
      :isLoading="isLoading"
      :warning="warning"
    />
  </div>
</template>
<script>
import PageHeader from "@/components/PageHeader.vue";
import PageTherapistClientPaymentPage from "./Payment/PageTherapistClientPayment.vue";
import PagePaymentError from "./Error/PageTherapistClientError.vue";
import PageTherapistClientSuccess from "../Onboarding/Success/PagePaymentSuccess.vue";
import PageTherapistClientPaymentDownload from "../Onboarding/Success/PagePaymentDownload.vue";
import {
  therapistClientPaymentFields,
  defaultTherapistClientFormState,
  PAYMENT_FORMS,
  MODULES,
  PAGE_TYPES,
} from "@/config";
import {
  createFormState,
  denormaliseTherapistClientPaymentForm,
  denormaliseOrderSummary,
} from "@/utils";
import * as queryString from "query-string";
import { pageInformationMixin } from "@/mixins/index.js";

export default {
  data() {
    return {
      error: null,
      warning: null,
      isLoading: false,
      isProduction: process.env.NODE_ENV === "production",
      pageTypes: PAGE_TYPES,
      whiteListedQueryParams: ["plan_id", "token", "user_id", "name"],
      [PAYMENT_FORMS.therapistClient]: createFormState(
        therapistClientPaymentFields,
        defaultTherapistClientFormState
      ),
    };
  },
  components: {
    PageHeader,
    PageTherapistClientPaymentPage,
    PageTherapistClientSuccess,
    PagePaymentError,
    PageTherapistClientPaymentDownload,
  },
  mixins: [pageInformationMixin],
  mounted() {
    const { dispatch } = this.$store;

    dispatch(`${MODULES.COUNTRY}/getCountriesAction`);

    dispatch(`${MODULES.PLAN}/getAllPlansAction`).then(() => {
      this.setInitialSearchParams((data) => {
        dispatch(`${MODULES.PAYMENT}/getSaleSummaryAction`, {
          item_id: data.plan_id,
          item_type: this.therapistClientForm.itemType.value,
          paymentType: "therapist_client",
        });
      });
    });
  },
  methods: {
    setError(err) {
      this.error = err;
    },
    setWarning(warning) {
      this.warning = warning;
    },
    setCurrentPage(item) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.APP}/setCurrentPage`, item);
    },
    setInitialSearchParams(callback) {
      const search = location.hash.replace("#/", "");

      if (search) {
        const obj = queryString.parse(search);

        const check = (key) => !obj[key] && key !== "name";

        if (this.whiteListedQueryParams.some(check)) {
          this.setError(
            "Oops, something went wrong with your link! Please contact support@ovrcome.io"
          );

          return;
        }

        Object.entries(obj)
          .filter((item) => this.whiteListedQueryParams.includes(item[0]))
          .forEach((entry) => {
            this.updateFormState(
              PAYMENT_FORMS.therapistClient,
              entry[0],
              entry[1],
              false
            );
          });

        callback(obj);
      }
    },
    updateFormState(formName, field, value, touched) {
      this[formName][field] = { value, touched };

      if (field === "countryId") {
        const payload = {
          country_id: value,
        };

        this.getCountryDeliveryMethods(formName, payload);
      }
    },
    getCountryStates(id) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.COUNTRY}/getCountryStatesById`, id);
    },
    getCountryDeliveryMethods(formName, payload) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.COUNTRY}/getCountryDeliveryMethods`, payload).then(
        (data) => {
          if (data.length > 0) {
            const payload = { delivery_method_id: data[0].id };

            this.getSaleSummary(formName, payload);
          }
        }
      );
    },
    getSaleSummary(formName, payload) {
      const { dispatch } = this.$store;
      const form = this[formName];

      let data = {
        ...denormaliseOrderSummary(form),
        ...payload,
        item_id: form.plan_id.value,
        item_type: form.itemType.value,
        paymentType: "therapist_client",
      };

      dispatch(`${MODULES.PAYMENT}/getSaleSummaryAction`, data).then(() => {
        if (payload?.delivery_method_id) {
          this.updateFormState(
            formName,
            "deliveryMethod",
            payload.delivery_method_id,
            false
          );
        }
      });
    },
    trackSuccessEvent() {
      const { user_id } = this[PAYMENT_FORMS.therapistClient];

      const payload = {
        Host: "Web",
      };
      try {
        if (window?.analytics) {
          window.analytics.identify(
            user_id.value,
            {
              ...payload,
            },
            {},
            () =>
              window.analytics.track("New User - Subscription Started", payload)
          );

          if (window?.dataLayer?.push()) {
            window.dataLayer.push({ event: "onboarding_purchased" });
          }
        }
      } catch (e) {
        this.chainWarning(e);
      }
    },
    chainWarning(err) {
      this.isLoading = false;
      this.warning = this.isProduction
        ? err?.paid
          ? "Oh no - we received your payment, but something else went wrong. Sorry about that! Please contact oVRcome support: support@ovrcome.io"
          : "Something went wrong. Please contact oVRcome support: support@ovrcome.io"
        : err?.message || err?.error;

      return Promise.reject(err);
    },
    submitStripeProcess: async function (stripe, paymentElement) {
      const {
        trackSuccessEvent,
        chainWarning,
        setCurrentPage,
        defaultProgression,
      } = this;

      const { dispatch } = this.$store;

      const form = this[PAYMENT_FORMS.therapistClient];
      let denormalisedPayload = denormaliseTherapistClientPaymentForm(form);

      Object.keys(denormalisedPayload).forEach((key) => {
        if (!denormalisedPayload[key]) {
          delete denormalisedPayload[key];
        }
      });

      this.isLoading = true;

      await stripe
        .createPaymentMethod({
          type: "card",
          card: paymentElement,
          billing_details: {
            name: form.name.value,
          },
        })
        .then((data) =>
          dispatch(`${MODULES.PAYMENT}/submitPayment`, {
            ...denormalisedPayload,
            payment_method_id: data.paymentMethod.id,
            paymentType: "therapist_client",
          })
            .then((data) =>
              dispatch(`${MODULES.PAYMENT}/confirmPayment`, {
                payment_intent_id: data.payment_intent_id,
                sale_id: data.sale_id,
                token: data.token,
                client_secret: data.client_secret,
                user_id: data.user_id,
                requires_action: false,
                paymentType: "therapist_client",
              })
                .then(() => (this.isLoading = false))
                .then(() => setCurrentPage(defaultProgression))
                .then(() => trackSuccessEvent())
                .catch(chainWarning)
            )
            .catch(chainWarning)
        )
        .catch(chainWarning);
    },
  },
};
</script>
